import yoda from '@kibt/bridge';
import { versionCompare } from '@kibt/web-utils/version';
import { appVersion, isAndroid, isIOS } from '../device-info';
import queryString from 'query-string';
import { radar } from '@/common/log';
import { getMinBundleVersionById } from '../getMinBundleVersion';
import { getAbHeight } from '@/hooks/abTest/useAbScreenHeight';
import { getFailOverUrl } from '@kibt/web-utils/fail-over';
import { navigation } from '@/Navigation';
import { goAddAddressWhenNoAddress, parseAddressUrl } from './parseAddressUrl';
import { covertToSpaUrl } from '../ab/isHitSpaAb';
import { getSource } from '../utils/setSource';
import { parseWebpack } from './parseWebpack';
import { debounce } from 'lodash';
import { abPromise } from '@/hooks/abTest/firstScreen/useFirstScreenAb';
import { getBundleInfo } from './getBundleInfo';

/**
 * 判断是否需要使用CCT Webview打开
 */
export function isNeedCCTWebview(url: string) {
    // 电商自建后三方商品更少了，使用黑名单来判断是否使用cct webview
    const blackList = [
        // 'taptapshop.com',
        // 'shopping.wed2c.com',
        'shopee.com',
        'aliexpress.com',
        'alicdn.com',
        'mercadolivre.com',
        'amzn.to',
        'amazon.com',
        'casasbahia.com',
        'americanas.com',
        'magazineluiza.com',
        'shein.com',
        'patpat.com',
        'popopieshop.com',
        'niuniudaddy.com',
        'akibaba.com',
        'salonline.com',
        'descomplica.com',
        'dreamriverclub.com',
        'shoptap.com',
        'myshopify.com',
        'shopify.com',
        'tec.br',
        'welowshop.com',
        'conectlifes.com',
        'voxely.com,amazonaws.com',
    ];

    let urlHost = url;
    if (url.indexOf('?') > -1) {
        const index = url.indexOf('?');
        urlHost = url.slice(0, index);
    }

    for (let i = 0; i < blackList.length; i++) {
        const item = blackList[i];
        if (urlHost.includes(item)) {
            return true;
        }
    }
    return false;
}

/**
 * 使用CCT webview打开，如果打开失败，使用系统浏览器打开
 * cct webview旧版本有bad case，不能用caniuse判断是否可用，需要用版本判断是否可用
 * 6.4.10及其以上才可以用
 */
export function openByCCT({ url }: { url: string }) {
    const res = versionCompare(appVersion, '6.4.10');
    if (res >= 0) {
        return yoda.component
            .openCCT({ url })
            .then(() => {
                radar.event({
                    name: 'OpenUrl_CCT_Success',
                    eventType: 'SHOP_KRN_CUSTOM_EVENT',
                    params: { url },
                });
            })
            .catch((err) => {
                radar.event({
                    name: 'OpenUrl_CCT_Success',
                    eventType: 'SHOP_KRN_CUSTOM_EVENT',
                    params: { url, err },
                });
                return yoda.tool.launchApp({
                    scheme: url,
                });
            });
    }
    console.log(`当前app版本为: ${appVersion}, 6.4.10及其以上才可以用cct webview`);
    return yoda.tool
        .launchApp({
            scheme: url,
        })
        .then(() => {
            radar.event({
                name: 'OpenUrl_LaunchApp_Success',
                eventType: 'SHOP_KRN_CUSTOM_EVENT',
                params: { url },
            });
        })
        .catch((err) => {
            radar.event({
                name: 'OpenUrl_LaunchApp_Success',
                eventType: 'SHOP_KRN_CUSTOM_EVENT',
                params: { url, err },
            });
            return Promise.reject(err);
        });
}

interface FullWebviewOptions {
    title?: string;
}
interface HalfWebviewOptions {
    launchType?: number;
    corner?: number;
    height?: number;
    fullScreen?: string;
}

function getFailBackUrl(url: string) {
    const targetUrl = getFailOverUrl(url);
    console.log('after getFailBackUrl', targetUrl);
    return targetUrl;
}

const addPageV2Query = async (url) => {
    const abInfo = await abPromise;
    console.log(abInfo.if_hit_orderpage_v2, 'if_hit_orderpage_v2');
    if (abInfo.if_hit_orderpage_v2?.value) {
        const newUrl = queryString.stringifyUrl({
            url: url,
            query: {
                pageV2: '1',
            },
        });
        return newUrl;
    }
    return url;
};

/**
 * 前端控制
 */
export async function openUrlV1(args: {
    url: string;
    halfWebview?: boolean;
    options?: FullWebviewOptions | HalfWebviewOptions;
    isNeedSetTimeout?: boolean;
    refererPage?: string;
    fullScreen?: string;
}) {
    const { url: _url, halfWebview = false, isNeedSetTimeout = false, options = {}, refererPage = '', fullScreen = '1' } = args;
    // const _urlV2 = getFailBackUrl(_url);
    const parseUrl = queryString.parseUrl(_url);
    let redirectUrl = parseUrl.query.redirectUrl;

    // 设置电池栏背景色透明(目前只有购物车和提单页在大促期间会添加这个参数)
    try {
        const redirectUrl2 = queryString.parse(redirectUrl as string);
        const bundleId = redirectUrl2.bundleId as string;
        if (['MyShoppingCar', 'EcommOrderConfirm'].includes(bundleId)) {
            redirectUrl2.themeStyle = '1';
        }
        redirectUrl = queryString.stringify(redirectUrl2);
    } catch (e) {
        console.error(e);
    }

    try {
        // ios在url上加上minBundleVersion，安卓通过kSwitch
        if (redirectUrl && isIOS) {
            const redirectUrlParams = queryString.parse(redirectUrl as string);
            const bundleId = redirectUrlParams?.bundleId;
            const minBundleVersion = await getMinBundleVersionById(bundleId);

            if (minBundleVersion) {
                redirectUrl = queryString.stringify({
                    ...redirectUrlParams,
                    minBundleVersion,
                });
            }
        }
    } catch (e) {
        console.error(e);
    }

    const query: any = {};
    if (redirectUrl) {
        // 若是半屏打开需增加特定参数
        const halfWebviewParams = halfWebview
            ? {
                  cornerRadius: 16,
                  height: 1, // iosrn 新增跳转参数
                  foldRatio: 0.75, // iosrn 新增跳转参数
                  openSlideFullScreen: 1, // iosrn 新增跳转参数 表明是可拖拽的半屏
                  isHalf: 1, // iosrn 新增跳转参数
              }
            : {};
        redirectUrl = queryString.stringify({
            ...halfWebviewParams,
            ...queryString.parse(redirectUrl as string),
        });
        query.redirectUrl = redirectUrl;
    }
    query.source = getSource();
    query.urlVersion = 1;

    let url = _url;

    // 如果命中SPA实验 且不是半屏场景
    if (!halfWebview) {
        const {
            isSpaUrl,
            options: { url: spaUrl, openNewContainer, navigationOptions },
        } = await covertToSpaUrl({ targetUrl: _url, targetContainerType: halfWebview ? 'halfScreen' : 'fullScreen' });
        if (isSpaUrl && !openNewContainer) {
            return navigation.push(navigationOptions.componentName, navigationOptions.query);
        } else {
            url = spaUrl;
        }
    }
    // MPA构建url
    else {
        url = queryString.stringifyUrl({
            url: _url,
            query,
        });
    }
    // 提单页V2.0
    url = await addPageV2Query(url);
    url = await parseAddressUrl(url);
    url = await goAddAddressWhenNoAddress(url);
    url = await parseWebpack(url);

    console.log('redirect to: ', url);

    //  判断是否需要使用cct webview打开
    if (isNeedCCTWebview(url)) {
        return openByCCT({ url });
    }

    if (halfWebview && appVersion.indexOf('9.10.10') === -1) {
        const newUrl = queryString.stringifyUrl({
            url,
            query: { webviewType: 'half' },
        });
        const height = isIOS ? 0.8 : await getAbHeight();
        return yoda.component
            .loadNewUrlHalfPage({
                url: newUrl,
                launchType: 1,
                corner: 16,
                height,
                fullScreen,
                hideToolbar: !isIOS,
                ...options,
            })
            .catch((err) => {
                radar.event({
                    name: 'OpenUrl_HalfWebview_Error',
                    eventType: 'SHOP_KRN_CUSTOM_EVENT',
                    params: { url, err, refererPage },
                });
                return yoda.webview
                    .open({
                        url,
                        launchOptions: {}, // 个别机型 不增加这个参数无法跳转
                        ...options,
                    })
                    .catch((err) => {
                        radar.event({
                            name: 'OpenUrl_FullWebview_Error',
                            eventType: 'SHOP_KRN_CUSTOM_EVENT',
                            params: { url, err, refererPage },
                        });
                        return Promise.reject(err);
                    });
            });
    } else {
        // ios 9.3.30 新增桥 https://team.corp.kuaishou.com/task/T3657574
        if (isIOS && versionCompare(appVersion, '9.3.30') >= 0) {
            return yoda.component
                .openFullScreenWebView({
                    url,
                    ...options,
                })
                .catch((err) => {
                    radar.event({
                        name: 'OpenUrl_FullScreenWebview_Error',
                        eventType: 'SHOP_KRN_CUSTOM_EVENT',
                        params: { url, err, refererPage },
                    });
                    return Promise.reject(err);
                });
        }

        // 这个if else 是为了解决在商详页跳到确认下单页时候的黑屏问题
        if (isNeedSetTimeout) {
            setTimeout(() => {
                return yoda.webview
                    .open({
                        url,
                        launchOptions: {}, // 个别机型 不增加这个参数无法跳转
                        ...options,
                    })
                    .catch((err) => {
                        radar.event({
                            name: 'OpenUrl_FullWebview_1_Error',
                            eventType: 'SHOP_KRN_CUSTOM_EVENT',
                            params: { url, err, refererPage },
                        });
                        return Promise.reject(err);
                    });
            }, 0);
        } else {
            return yoda.webview
                .open({
                    url,
                    launchOptions: {}, // 个别机型 不增加这个参数无法跳转
                    ...options,
                })
                .catch((err) => {
                    radar.event({
                        name: 'OpenUrl_FullWebview_2_Error',
                        eventType: 'SHOP_KRN_CUSTOM_EVENT',
                        params: { url, err, refererPage },
                    });
                    return Promise.reject(err);
                });
        }
    }
}

/**
 * @param options 前端处理的打开容器的参数
 * @returns viod
 * @warning option里不能随意增加字段，能让后端下发尽量让后端下发
 */
type frontEndAllowedOptions = {
    hideToolbar: boolean;
    corner: number;
};
/**
 * 后端下发url
 * @param url 跳转的url，由后端下发
 * @returns viod
 */
export async function openUrlV2({ url, options }: { url: string; options?: frontEndAllowedOptions }) {
    const newUrl = await parseWebpack(url);
    let urlWithSource = queryString.stringifyUrl({
        url: newUrl,
        query: {
            source: getSource(),
            urlVersion: 2,
        },
    });
    // 提单页V2.0
    urlWithSource = await addPageV2Query(urlWithSource);
    const parseUrl = queryString.parseUrl(urlWithSource);
    const redirectUrl = parseUrl.query.redirectUrl;

    const redirectUrlQuerys = queryString.parse((redirectUrl as string) || '');
    const h5ContinerInit = parseUrl.query.__h5_container_init__ ? JSON.parse(parseUrl.query.__h5_container_init__ as string) : {};

    const isHalf = (redirectUrlQuerys.bundleId && Number(redirectUrlQuerys.isHalf) === 1) || Number(h5ContinerInit.launchType);

    // 全屏场景
    if (!isHalf) {
        // 全屏场景判断是否命中SPA实验
        const {
            isSpaUrl,
            options: { url: spaUrl, openNewContainer, navigationOptions },
        } = await covertToSpaUrl({ targetUrl: urlWithSource, targetContainerType: isHalf ? 'halfScreen' : 'fullScreen' });
        // 如果命中SPA实验
        if (isSpaUrl && !openNewContainer) {
            return navigation.push(navigationOptions.componentName, navigationOptions.query);
        } else {
            urlWithSource = spaUrl;
        }
    }

    // 打开RN半屏容器
    if (redirectUrlQuerys.bundleId && Number(redirectUrlQuerys.isHalf) === 1) {
        // console.log('redirectUrlQuerys', redirectUrlQuerys, typeof redirectUrlQuerys.isHalf, parseUrl); // {"bundleId": "Detail", "componentName": "Detail", "height": "1", "isHalf": "0"}
        let loadNewHalfPageConfig;
        if (isIOS) {
            loadNewHalfPageConfig = {
                url: urlWithSource,
                hideToolbar: false,
                ...options,
            };
        } else {
            // 对字段做映射
            const iosMapToAndroidConfig = (querys: { isHalf: number; foldRatio: number; openSlideFullScreen: number }) => {
                return {
                    launchType: String(querys.isHalf),
                    height: String(querys.foldRatio),
                    fullScreen: String(querys.openSlideFullScreen),
                };
            };
            // 通过桥的参数打开loadNewHalfPage
            loadNewHalfPageConfig = {
                url: urlWithSource,
                hideToolbar: true,
                ...iosMapToAndroidConfig(redirectUrlQuerys as any),
                ...options,
            };
        }
        return yoda.component.loadNewUrlHalfPage(loadNewHalfPageConfig).catch(() => {
            radar.event({
                name: 'BackendUrlOpnenError',
                params: { url: urlWithSource, loadNewHalfPageConfig, bridge: 'loadNewUrlHalfPage', platform: isAndroid ? 'Android' : 'IOS' },
            });
            return yoda.webview
                .open({
                    url: urlWithSource,
                    launchOptions: {}, // 个别机型 不增加这个参数无法跳转
                    ...options,
                })
                .catch((err) => {
                    radar.event({
                        name: 'BackendUrlOpnenError',
                        params: { url: urlWithSource, bridge: 'loadNewUrlHalfPage', platform: isAndroid ? 'Android' : 'IOS' },
                    });
                    return Promise.reject(err);
                });
        });
    }
    // 打开h5半屏
    else if (Number(h5ContinerInit.launchType)) {
        // 读h5ContinerInit，通过loadNewHalfPage打开新页面
        yoda.component.loadNewUrlHalfPage({
            url: urlWithSource,
            ...h5ContinerInit,
        });
    }
    // 打开全屏
    else {
        // 打开全屏
        if (isIOS) {
            // ios 使用 openFullScreenWebView 打开全屏容器
            if (isIOS && versionCompare(appVersion, '9.3.30') >= 0) {
                return yoda.component
                    .openFullScreenWebView({
                        url: urlWithSource,
                        ...options,
                    })
                    .catch((err) => {
                        radar.event({
                            name: 'BackendUrlOpnenError',
                            params: { url: urlWithSource, bridge: 'openFullScreenWebView', platform: isAndroid ? 'Android' : 'IOS' },
                        });
                        return Promise.reject(err);
                    });
            }
        }
        return yoda.webview
            .open({
                url: urlWithSource,
                launchOptions: {}, // 个别机型 不增加这个参数无法跳转
                ...options,
            })
            .catch((err) => {
                radar.event({
                    name: 'BackendUrlOpnenError',
                    params: { url: urlWithSource, bridge: 'open', platform: isAndroid ? 'Android' : 'IOS' },
                });
                return Promise.reject(err);
            });
    }
}

function openUrlPure(
    paramsV1: {
        url: string;
        halfWebview?: boolean;
        options?: FullWebviewOptions | HalfWebviewOptions;
        isNeedSetTimeout?: boolean;
        refererPage?: string;
        fullScreen?: string;
    },
    paramsV2?: {
        url: string;
        options?: frontEndAllowedOptions;
    },
) {
    // 打开新页面时，增加全局事件，有一些业务倒计时判断，如果打开新页面倒计时关闭，因为桥的打开不确定，有准确率问题，暂时忽略准确率问题
    const { bundleId, componentName } = getBundleInfo();
    if (bundleId || componentName) {
        yoda?.event &&
            yoda.event.dispatchGlobalEvent({
                type: 'ecommerce://onOpenNewPage',
                data: { bundleId, componentName },
            });
    }

    // 如果后端下发了url，走后端的下发链接
    if (paramsV2?.url) {
        return openUrlV2(paramsV2);
    } else {
        return openUrlV1(paramsV1);
    }
}
// 加入800ms防抖，解决线上点击跳转按钮时多次打开RN容器问题
export const openUrl = debounce(openUrlPure, 800, {
    trailing: false,
    leading: true,
    maxWait: 1500,
});
